import { api, type Entry } from './api';

function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function getExportFilename(ext: string): string {
  const today = new Date().toISOString().slice(0, 10);
  return `hitorigoto-export-${today}.${ext}`;
}

function cleanEntry(entry: Entry): Record<string, unknown> {
  const { image_urls, updated_at, ...rest } = entry;
  const cleaned = { ...rest } as Record<string, unknown>;
  delete cleaned['embedding'];
  delete cleaned['user_id'];
  return cleaned;
}

export async function exportAsJSON(): Promise<void> {
  const entries = await api.getEntries();
  const cleaned = entries.map(cleanEntry);
  const json = JSON.stringify(cleaned, null, 2);
  downloadFile(json, getExportFilename('json'), 'application/json');
}

export async function exportAsCSV(): Promise<void> {
  const entries = await api.getEntries();

  const headers = [
    'id', 'date', 'title', 'icon', 'mood', 'content', 'summary',
    'ai_comment', 'ai_insight', 'tags', 'sentiment_score',
    'duration_seconds', 'created_at',
  ];

  const escapeCSV = (value: unknown): string => {
    if (value == null) return '';
    const str = String(value);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const rows = entries.map(entry => {
    return headers.map(h => {
      if (h === 'tags') {
        return escapeCSV((entry.tags || []).join(';'));
      }
      return escapeCSV(entry[h as keyof Entry]);
    }).join(',');
  });

  // BOM for Excel compatibility
  const csv = '\uFEFF' + [headers.join(','), ...rows].join('\n');
  downloadFile(csv, getExportFilename('csv'), 'text/csv;charset=utf-8');
}

export async function exportAsMarkdown(): Promise<void> {
  const entries = await api.getEntries();

  // Group by date
  const byDate = new Map<string, Entry[]>();
  for (const entry of entries) {
    const date = entry.date;
    if (!byDate.has(date)) byDate.set(date, []);
    byDate.get(date)!.push(entry);
  }

  const lines: string[] = ['# ひとりごと日記エクスポート', ''];

  for (const [date, dateEntries] of byDate) {
    lines.push(`## ${date}`, '');
    for (const entry of dateEntries) {
      if (entry.title) lines.push(`### ${entry.icon || ''} ${entry.title}`, '');
      if (entry.content) lines.push(entry.content, '');

      const meta: string[] = [];
      if (entry.mood) meta.push(`気分: ${entry.mood}`);
      if (entry.tags?.length) meta.push(`タグ: ${entry.tags.join(', ')}`);
      if (entry.sentiment_score != null) meta.push(`感情スコア: ${entry.sentiment_score}`);
      if (entry.duration_seconds != null) {
        const min = Math.floor(entry.duration_seconds / 60);
        const sec = entry.duration_seconds % 60;
        meta.push(`録音時間: ${min}分${sec}秒`);
      }

      if (meta.length) lines.push(`> ${meta.join(' | ')}`, '');

      if (entry.summary) lines.push(`**要約:** ${entry.summary}`, '');
      if (entry.ai_comment) lines.push(`**AIコメント:** ${entry.ai_comment}`, '');
      if (entry.ai_insight) lines.push(`**AIインサイト:** ${entry.ai_insight}`, '');

      lines.push('---', '');
    }
  }

  downloadFile(lines.join('\n'), getExportFilename('md'), 'text/markdown;charset=utf-8');
}
